import { memo, type MutableRefObject, type RefObject } from 'react'
import { twMerge } from 'tailwind-merge'
import type { UserMessage } from '../../../../types'
import { MessageAttachments } from './message-attachments'
import { MessageEditable } from './message-editable'
import { MessageMarkdown } from './message-markdown'

const BUBBLE_CLASS_NAME =
  'ring-border/10 text-foreground bg-background/80 max-w-[80%] rounded-xl rounded-br-md px-4 py-2 ring-1'

const HIDDEN_ATTACHMENT_TYPES = new Set(['aboutMe', 'stagedActionEdit', 'integrationFocus'])

/**
 * Whether a user message carries anything that renders as an attachment chip.
 * `aboutMe`, `stagedActionEdit` and `integrationFocus` are plumbing the user never sees, so a
 * message carrying only those still counts as attachment-free.
 * (`stagedActionEdit` exists only as finalized attached context — the
 * `PendingAttachment` union has no such variant, so the pending path needs no
 * filter for it.)
 */
export function hasAttachment(message: UserMessage): boolean {
  const visibleAttachments = message.attached_context?.filter(
    (attachment) => !HIDDEN_ATTACHMENT_TYPES.has(attachment.type),
  )
  if (visibleAttachments && visibleAttachments.length > 0) return true

  const visiblePending = message.pending_context?.filter((a) => a.pending?.type !== 'stagedAction')
  if (visiblePending && visiblePending.length > 0) return true

  return !!(
    message.screenshot ||
    message.clipboard_text ||
    message.window_context ||
    message.window ||
    message.file_title ||
    message.audio ||
    message.image_url ||
    (message.file_attachments && message.file_attachments.length > 0)
  )
}

/** The attachment chips to render above the bubble — pending wins over settled. */
export function visibleAttachments(message: UserMessage) {
  const filteredPending = message.pending_context?.filter((a) => a.pending?.type !== 'stagedAction')
  if (filteredPending && filteredPending.length > 0) return filteredPending
  return (
    message.attached_context
      ?.filter((attachment) => !HIDDEN_ATTACHMENT_TYPES.has(attachment.type))
      ?.map((attachment) => ({ attachment })) ?? []
  )
}

export interface UserBubbleProps {
  message: UserMessage
  shouldAnimate?: boolean
  containsAttachment: boolean
  isEditMode: boolean
  formattedContent: string
  editedTextRef: MutableRefObject<string>
  onSaveEdit: () => void
  onCancelEdit: () => void
  /** Source element for the edit-mode HTML snapshot. */
  contentRef: RefObject<HTMLDivElement>
}

export const UserBubble = memo(function UserBubble({
  message,
  shouldAnimate,
  containsAttachment,
  isEditMode,
  formattedContent,
  editedTextRef,
  onSaveEdit,
  onCancelEdit,
  contentRef,
}: UserBubbleProps) {
  return (
    <>
      {containsAttachment && (
        <div className="flex w-full justify-end">
          <div className="flex max-w-[80%] flex-1 items-center justify-end">
            <MessageAttachments
              messageId={message.id}
              conversationId={message.conversation_id}
              attachments={visibleAttachments(message)}
            />
          </div>
        </div>
      )}

      <div className="flex flex-col items-end">
        {!!message.content &&
          (isEditMode ? (
            <MessageEditable
              id={message.id}
              initialHTML={formattedContent}
              className={twMerge(
                'ring-border/30 bg-background/80 text-foreground w-full max-w-[80%] rounded-xl rounded-br-md px-4 py-2 ring-1',
              )}
              onChange={(text) => {
                editedTextRef.current = text
              }}
              onSubmit={onSaveEdit}
              onCancel={onCancelEdit}
            />
          ) : typeof message.content === 'string' ? (
            <MessageMarkdown
              ref={contentRef}
              content={message.content}
              messageId={message.id}
              mentions={message.mentions}
              shouldAnimate={shouldAnimate}
              className={BUBBLE_CLASS_NAME}
            />
          ) : (
            <div ref={contentRef} className={BUBBLE_CLASS_NAME}>
              {message.content}
            </div>
          ))}
      </div>
    </>
  )
})
